import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Stack } from "@mui/material";
import moment from "moment";
import { ActionTypes } from "../../../OrderFlowProvider/OrderFlowActionTypes";
import { useOrderFlow } from "../../../OrderFlowProvider/OrderFlowContext";
import { ModuleNames } from "../../../OrderFlowProvider/OrderFlowTypes";
import { clone } from "../../../../Shared/utils/clone";
/**
 * useDaysOfWeek - Handles filtering products and recipes by day of the week which also affects calendar start day. Also contains a helper function to convert the dayEnum to the configured momentjs string format
 * @param moduleConfig
 * @returns handleDaysOfWeekChange, dayLabel enum to momentjs string formatter
 */
const useDaysOfWeek = (sequenceNumber) => {
    var _a;
    const { store, dispatch } = useOrderFlow();
    const moduleConfig = (_a = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.DAYS_OF_WEEK &&
        module.Sequence === sequenceNumber &&
        module.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    const handleDaysOfWeekChange = (selectedDay) => {
        let daysOfWeek = [...store.cart.daysOfWeek];
        if (!daysOfWeek.includes(selectedDay)) {
            daysOfWeek.push(selectedDay);
        }
        else {
            daysOfWeek = daysOfWeek.filter(function (day) {
                return day != selectedDay;
            });
            /* Update start date if necessary */
            if (store.cart.startDate && moment(store.cart.startDate).day() === selectedDay) {
                const mealDaysForStartDate = store.model.DeliveryDays.filter((dd) => moment(dd.ShipDateString).isSame(store.cart.startDate, "date")).map((dd) => moment(dd.MealDateString).day());
                if (!daysOfWeek.some((day) => mealDaysForStartDate.includes(day))) {
                    const updatedMealDates = store.model.DeliveryDays.filter(function (day) {
                        return daysOfWeek.includes(moment(day.MealDate).day());
                    }).map((day) => moment(day.MealDate));
                    if (updatedMealDates.length > 0) {
                        dispatch({
                            type: ActionTypes.SET_START_DATE,
                            payload: updatedMealDates[0],
                        });
                    }
                }
            }
        }
        dispatch({ type: ActionTypes.SET_DAYS_OF_WEEK, payload: daysOfWeek });
    };
    const dayLabel = (dayEnum) => {
        const dayFormat = moduleConfig.DayOfWeekFormat;
        const day = moment().day(dayEnum);
        return dayFormat.map((formatString) => moment(day).format(formatString));
    };
    // If StartSunday == False, move Sunday to the end of the list
    const sortedDeliveryDays = clone(store.model.DeliveryScheduleMealDays);
    sortedDeliveryDays.sort(function (a, b) {
        if (store.model.StartSunday == false) {
            if (a == 0)
                return 1;
            if (b == 0)
                return -1;
        }
        return a - b;
    });
    return {
        moduleConfig,
        deliveryScheduleMealDays: sortedDeliveryDays,
        daysOfWeek: store.cart.daysOfWeek,
        handleDaysOfWeekChange,
        dayLabel,
    };
};
const daybuttonStyles = {
    fontSize: "20px",
    margin: "8px",
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "auto",
};
/**
 * DaysOfWeek - Renders the DaysOfWeek module: A list of days based on the moduleConfig and delivery schedule days.
 * @returns DaysOfWeek module
 */
export const DaysOfWeek = ({ sequenceNumber }) => {
    const { moduleConfig, deliveryScheduleMealDays, daysOfWeek, handleDaysOfWeekChange, dayLabel } = useDaysOfWeek(sequenceNumber);
    return (_jsxs(Box, Object.assign({ width: "100%" }, { children: [!moduleConfig.Visible && _jsx(_Fragment, {}), moduleConfig.Visible && (_jsx(Stack, Object.assign({ direction: "row", flexWrap: "wrap", justifyContent: "center" }, { children: deliveryScheduleMealDays.map((day) => {
                    const filterSelected = daysOfWeek.includes(day);
                    return (_jsx(Button, Object.assign({ sx: daybuttonStyles, color: "primary", variant: filterSelected ? "contained" : "outlined", onClick: () => handleDaysOfWeekChange(day), disabled: !moduleConfig.CustomerEditable }, { children: dayLabel(day) }), day));
                }) })))] })));
};
